import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProjectService } from '../../services/project.service';
import { StrategyService } from '../../services/strategy.service';
import { Subject, switchMap, take, takeUntil, filter } from 'rxjs';
import { Strategy } from '../../interfaces/models/strategy.model';
import { IProject } from '../../interfaces/models/project.model';
import { TagModule } from 'primeng/tag';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { SocketService } from '../../services/socket.service';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { SubstringPipe } from '../../pipes/substring.pipe';
import { TooltipModule } from 'primeng/tooltip';
import { SharedProjectComponent } from '../../shared/components/shared-project/shared-project.component';
import { PaginatorComponent } from '../../shared/paginator/paginator.component';
import { SkeletonModule } from 'primeng/skeleton';
import { CustomAblePipe } from '../../pipes/able.pipe';
import { authUser } from '../../interfaces';
import { AuthService } from '../../services/auth.service';
import { TableModule } from 'primeng/table';
import { AdminService } from '../../services/admin.service';
export interface IPaginator {
  page: number
  page_size: number
  no_records: number
  total_records: number
  total_pages: number
}


import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-strategic-project',
  standalone: true,
  imports: [
    NavTitlePipe,
    ButtonModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    CommonModule,
    RouterModule,
    DropdownModule,
    FormsModule,
    ReactiveFormsModule,
    RadioButtonModule,
    TagModule,
    ConfirmPopupModule,
    TranslateModule,
    SubstringPipe,
    TooltipModule,
    SharedProjectComponent,
    PaginatorComponent,
    SkeletonModule,
    TableModule,



  ],
  providers: [ConfirmationService],
  templateUrl: './strategic-project.component.html',
  styleUrl: './strategic-project.component.scss'
})
export class StrategicProjectComponent implements OnInit, OnDestroy {
  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;
  private unsubscribe$ = new Subject<void>();

  strategyId = 0;
  strategy = {} as Strategy;
  hoveredProjectId = 0;
  isLoading = true;
  errorMessage = '';
  displayType = 'list'; // Default to table view

  projectStatusListEn = [
    {
      label: "all",
      value: "all"
    },
    {
      label: "draft",
      value: "draft"
    },
    {
      label: "waiting for approval",
      value: "waiting_for_approval"
    },
    {
      label: "pending",
      value: "pending"
    },
    {
      label: "approved",
      value: "approved"
    },
    {
      label: "rejected",
      value: "rejected"
    },
    {
      label: "rejected by department",
      value: "rejected_by_department_manager"
    }
  ]
  projectStatusListAr = [
    { label: 'الكل', value: 'all' },
    { label: 'مسودة', value: 'draft' },
    { label: 'بانتظار الموافقة', value: 'waiting_for_approval' },
    { label: 'قيد التقييم', value: 'pending' },
    { label: 'معتمدة', value: 'approved' },
    { label: 'مرفوضة', value: 'rejected' },
    { label: 'مرفوضة من مدير الإدارة', value: 'rejected_by_department_manager' }
  ]
  ProjectStatusList = [] as any[]



  set selectedProjectStatus(status: any) {
    this._selectedProjectStatus = status
  }
  get selectedProjectStatus() {
    return this._selectedProjectStatus;
  }
  set searchTerm(searchTerm: string) {
    this._searchTerm = searchTerm;
  }
  get searchTerm() {
    return this._searchTerm;
  }
  dir!: 'rtl' | 'ltr';
  _selectedProjectStatus = { "value": "all", "label": "all" }
  projects = [] as IProject[];
  _searchTerm = ''
  filteredProjects = [] as IProject[];
  strategyOptions = [] as any[]
  selectedSortOption = ''
  projectsScope = 'all'
  departmentsAndSections = [] as any[];
  selectedOrgUnitId: number | string = 'all';
  selectedOrgUnitType: 'all' | 'department' | 'section' = 'all';
  sortOptions = [
    { value: 'latest-added', label: 'تاريخ الإضافة : الأحدث فالأقدم' },
    { value: 'oldest-added', label: 'تاريخ الإضافة : الأقدم فالأحدث' },
    { value: 'latest-start', label: 'تاريخ البداية : الأحدث فالأقدم' },
    { value: 'oldest-start', label: 'تاريخ البداية : الأقدم فالأحدث' },
    { value: 'descending', label: 'أبجدي : الألف إلى الياء' },
    { value: 'ascending', label: 'أبجدي : الياء إلى الألف' }
  ];
  constructor(
    private projectService: ProjectService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private confirmationService: ConfirmationService,
    private socketService: SocketService,
    private directionService: DirectionService,
    private authUser: AuthService,
    private adminService: AdminService
  ) {
  }
  user!: authUser;

  ngOnInit(): void {
    this.directionService.direction$.pipe(takeUntil(this.unsubscribe$)).subscribe((dir) => {
      this.dir = dir;
      if (dir == 'rtl') {
        this.ProjectStatusList = this.projectStatusListAr
      } else {
        this.ProjectStatusList = this.projectStatusListEn
      }

      const currentValue = this._selectedProjectStatus?.value || 'all';
      this._selectedProjectStatus = this.ProjectStatusList.find((s: any) => s.value === currentValue) || this.ProjectStatusList[0];
      
      // Update org unit labels based on direction
      if (this.departmentsAndSections.length > 0) {
        this.departmentsAndSections[0].label = this.dir === 'rtl' ? 'كل الإدارات والمكاتب' : 'All Departments & Sections';
      }
    })
    this.route.data.pipe(takeUntil(this.unsubscribe$)).subscribe(({ projectPageData }) => {
      try {
        if (!projectPageData) {
          throw new Error('projectPageData is undefined');
        }

        // The API response structure is: { success, data: { projects: [...], pagination: {...} } }
        const apiResponse = projectPageData.projects;
        
        if (apiResponse && apiResponse.data) {
          this.projects = apiResponse.data.projects || [];
          this.paginator = apiResponse.data.pagination || {} as IPaginator;
          console.log('📊 Projects loaded:', {
            'في هذه الصفحة': this.projects.length,
            'الإجمالي': this.paginator.total_records,
            'الصفحة الحالية': this.paginator.page,
            'إجمالي الصفحات': this.paginator.total_pages
          });
        } else {
          console.warn('No data in API response');
          this.projects = [];
          this.paginator = {} as IPaginator;
        }

        this.strategyId = projectPageData.strategy?.id || 0;
        this.strategy = projectPageData.strategy || {} as Strategy;
        this.filteredProjects = structuredClone(this.projects);
        this.strategyOptions = (projectPageData.strategies || [])
          .map((x: Strategy) => ({ name: x.name, strategyId: x.id }));
        
        this.isLoading = false;
      } catch (error) {
        console.error('Error processing projectPageData:', error);
        this.errorMessage = 'Failed to load project data. Please check the console for details.';
        this.projects = [];
        this.filteredProjects = [];
        this.paginator = {} as IPaginator;
        this.isLoading = false;
      }
    })

    this.authUser.authBufferSub$.subscribe((user) => {
      this.user = user as authUser;
    })

    // Load departments and build combined list with sections
    this.adminService.getListDepartments().pipe(takeUntil(this.unsubscribe$)).subscribe((response: any) => {
      const departmentsData = response['data']['departments'] || [];
      
      // Build combined list
      const combinedList = [
        { 
          id: 'all', 
          type: 'all',
          label: this.dir === 'rtl' ? 'كل الإدارات والمكاتب' : 'All Departments & Sections',
          indent: false
        }
      ];
      
      departmentsData.forEach((dept: any) => {
        // Add department
        combinedList.push({
          id: dept.id,
          type: 'department',
          label: dept.name,
          indent: false
        });
        
        // Add sections under department
        if (dept.units && dept.units.length > 0) {
          dept.units.forEach((unit: any) => {
            combinedList.push({
              id: unit.id,
              type: 'section',
              label: `   • ${unit.name}`,
              indent: true
            });
          });
        }
      });
      
      this.departmentsAndSections = combinedList;
      
      // After departments are loaded, fetch projects with current filter state
      this.fetchProjects();
    });

    // âœ… Listen for navigation events to refresh data when coming back to this page
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.unsubscribe$)
    ).subscribe((event: any) => {
      // Check if we navigated to the projects page
      if (event.url.includes('/strategies/projects') || event.url.includes('/strategies/') && event.url.split('/').length === 3) {
        console.log('Navigated to projects page - refreshing data...');
        this.fetchProjects();
      }
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }


  onMouseEnter(hoveredProjectId: number) {
    this.hoveredProjectId = hoveredProjectId;
  }
  onMouseLeave() {
    this.hoveredProjectId = 0;
  }

  sort() {
    this._selectedProjectStatus = { "value": "all", "label": "all" };
    const sortBy = this.selectedSortOption;
    const attributeKey = sortBy.includes("start") ? "start_date" : "created_at";
    if (["latest-start", "oldest-start", "latest-added", "oldest-added"].includes(sortBy)) {
      this.filteredProjects = this.filteredProjects.sort((a, b) => {
        const [aStartDate, bStartDate] = [
          new Date(a[attributeKey]).getTime(),
          new Date(b[attributeKey]).getTime(),
        ]
        return sortBy.includes("latest") ? bStartDate - aStartDate : aStartDate - bStartDate
      })
    };

    if (["descending", "ascending"].includes(sortBy)) {
      this.filteredProjects = this.projects.sort((a, b) => {
        const [aName, bName] = [
          a.name,
          b.name,
        ]
        return sortBy.includes("descending") ? aName.localeCompare(bName, 'ar') : bName.localeCompare(aName, 'ar')
      })
    }

  }

  filterBySearch() {
    // Client-side search on current page results
    if (!this.searchTerm || !this.searchTerm.trim()) {
      this.filteredProjects = structuredClone(this.projects);
      return;
    }
    this.filteredProjects = this.projects.filter(({ name, department_name, section_name }) => {
      return [name, department_name, section_name].join(" ").toLowerCase().includes(this.searchTerm.toLowerCase());
    });
  }
  
  getFilteredStatusList() {
    // Filter out draft status if viewing others' projects
    if (this.projectsScope === 'others') {
      return this.ProjectStatusList.filter((status: any) => status.value !== 'draft');
    }
    return this.ProjectStatusList;
  }
  strategyOptionsChange() {
    this.pageNumber = 1;
    this.isLoading = true;
    this.fetchProjects();
    this.socketService.setRefreshNotification(this.strategyId)
  }
  confirmProjectDelete(event: Event, projectId: number) {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    this.confirmationService.confirm({
      target: target,
      message: this.dir == 'rtl' ? 'هل تريد تأكيد المسح؟' : 'Do you want to delete this project?',
      accept: () => {
        this.projectService.deleteProject(+this.strategyId, projectId).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          // Refresh current page after deletion
          this.fetchProjects();
          this.toastr.success(this.dir == 'rtl' ? 'تمت المسح' : 'The Project has been deleted successfully.', this.dir == 'rtl' ? 'عملية ناجحة' : 'Success');
        })
      },
      reject: () => {
        this.toastr.info(this.dir == 'rtl' ? 'تم الالغاء' : 'The deletion has been canceled.', this.dir == 'rtl' ? 'الغاء' : 'Canceled');
      }
    });
  }
  accept() {
    this.confirmPopup.accept();
  }
  reject() {
    this.confirmPopup.reject();
  }
  selectProjectStatus(projectStatus: any) {
    this.selectedProjectStatus = projectStatus;
    this.pageNumber = 1; // Reset to first page when filtering
    this.isLoading = true;
    this.fetchProjects();
  }
  pageNumber = 1;
  page_size = 20;
  paginator = {} as IPaginator;
  pageChangeHandler(page: number) {
    console.log('Page change to:', page);
    this.pageNumber = page;
    this.isLoading = true;
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetchProjects();
  }
  pageSizeChangeHandler(pageSize: number) {
    this.page_size = pageSize;
    this.isLoading = true;
    this.fetchProjects();
  }
  changeScope(scope: string) {
    this.projectsScope = scope;
    this.isLoading = true;
    this.fetchProjects();
  }


  orgUnitChange() {
    // Find selected item to get its type
    const selectedItem = this.departmentsAndSections.find(item => item.id === this.selectedOrgUnitId);
    
    if (selectedItem) {
      this.selectedOrgUnitType = selectedItem.type;
      console.log('Selected org unit:', {
        id: this.selectedOrgUnitId,
        type: this.selectedOrgUnitType,
        label: selectedItem.label
      });
    }
    
    this.pageNumber = 1;
    this.isLoading = true;
    this.fetchProjects();
  }

  private fetchProjects() {
    console.log('Fetching projects - Page:', this.pageNumber, 'PageSize:', this.page_size, 'Scope:', this.projectsScope);
    
    const filters = this.selectedProjectStatus.value == 'all' ? '' : this.selectedProjectStatus.value;
    
    // Backend only supports: page, page_size, scope, filters (status)
    // Department/section filtering will be done client-side
    const requestParams: any = { 
      page: this.pageNumber, 
      page_size: this.page_size, 
      scope: this.projectsScope
    };
    
    // Only add filters parameter if it's not empty
    if (filters) {
      requestParams.filters = filters;
    }
    
    console.log('API Request Parameters:', requestParams);
    console.log('Client-side filter:', {
      orgUnitType: this.selectedOrgUnitType,
      orgUnitId: this.selectedOrgUnitId
    });
    
    this.projectService.getListPaginatedProjects(this.strategyId, requestParams).subscribe({
      next: (data: any) => {
        console.log('Projects fetched successfully:', data);
        let allProjects = data['data']['projects'] as IProject[];
        
        // Get original pagination from API
        this.paginator = data['data']['pagination'] as IPaginator;
        
        // Apply client-side department/section filtering ONLY if a specific unit is selected
        if (this.selectedOrgUnitType !== 'all' && this.selectedOrgUnitId !== 'all') {
          allProjects = this.filterProjectsByOrgUnit(allProjects);
          
          // ONLY update paginator when we actually filtered client-side
          this.paginator.total_records = allProjects.length;
          this.paginator.no_records = allProjects.length;
          this.paginator.total_pages = 1; // All filtered results on one page
        }
        
        this.projects = allProjects;
        this.filteredProjects = structuredClone(this.projects);
        
        console.log('📊 Projects after filtering:', {
          'في هذه الصفحة': this.projects.length,
          'الإجمالي': this.paginator.total_records,
          'عدد الصفحات': this.paginator.total_pages,
          'الصفحة': this.paginator.page
        });
        this.isLoading = false;
        this.errorMessage = '';
      },
      error: (error) => {
        console.error('Error fetching projects:', error);
        this.errorMessage = 'Failed to load projects. Please try again.';
        this.isLoading = false;
        this.toastr.error(
          this.dir == 'rtl' ? 'فشل تحميل المشاريع' : 'Failed to load projects',
          this.dir == 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }
  
  private filterProjectsByOrgUnit(projects: IProject[]): IProject[] {
    console.log('Filtering projects by org unit...');
    console.log('Selected org unit:', { type: this.selectedOrgUnitType, id: this.selectedOrgUnitId });
    
    const filtered = projects.filter(project => {
      // Check if project has stakeholder information
      const stakeholder = (project as any).stakeholder;
      
      if (!stakeholder) {
        console.log('Project has no stakeholder:', project.name);
        return false;
      }
      
      console.log('Project stakeholder:', {
        project: project.name,
        entityType: stakeholder.entity_in_charge_type,
        entityId: stakeholder.entity_in_charge_id
      });
      
      if (this.selectedOrgUnitType === 'department') {
        // For department: include projects directly under department OR under its sections
        if (stakeholder.entity_in_charge_type === 'department' && 
            stakeholder.entity_in_charge_id === this.selectedOrgUnitId) {
          return true;
        }
        
        // Also include projects from sections under this department
        // Check if the stakeholder section belongs to the selected department
        if (stakeholder.entity_in_charge_type === 'section') {
          // Get the selected department from the departments data
          const selectedDeptData = this.departmentsAndSections.find(d => 
            d.id === this.selectedOrgUnitId && d.type === 'department'
          );
          
          if (selectedDeptData) {
            // Check if the stakeholder section is in the departmentsAndSections list
            // (sections are added right after their parent department)
            let isDepartmentSection = false;
            let foundDept = false;
            
            for (const item of this.departmentsAndSections) {
              if (item.id === this.selectedOrgUnitId && item.type === 'department') {
                foundDept = true;
                continue;
              }
              
              if (foundDept && item.type === 'section') {
                if (item.id === stakeholder.entity_in_charge_id) {
                  isDepartmentSection = true;
                  break;
                }
              }
              
              if (foundDept && item.type === 'department') {
                // We've reached the next department
                break;
              }
            }
            
            return isDepartmentSection;
          }
        }
      } else if (this.selectedOrgUnitType === 'section') {
        // For section: only include projects directly under this section
        return stakeholder.entity_in_charge_type === 'section' && 
               stakeholder.entity_in_charge_id === this.selectedOrgUnitId;
      }
      
      return false;
    });
    
    console.log(`Filtered from ${projects.length} to ${filtered.length} projects`);
    return filtered;
  }

  clearSearch() {
    this.searchTerm = '';
    this.filterBySearch();
  }

  getStatusCount(status: string): number {
    return (this.projects || []).filter((p: any) => {
      const value = p?.assessment_status ?? p?.status;
      return String(value || '').toLowerCase() === status.toLowerCase();
    }).length;
  }
}




